'use client';

import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { Alert, Flex, Button, ButtonGroup } from '@/_components';
import { useCallback, useContext, useEffect, useState, type FC } from 'react';
import { usePolling } from '@/_hooks/usePolling';
import { resetFirmQuestionnaireBacklog } from '@/_api/admin-firms';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import { EnqueuedQuestionnairesDialog } from './components/EnqueuedQuestionnairesDialog';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

const QUEUE_COUNT_REFRESH_INTERVAL = 3000 as const;
const TABLE_REFRESH_INTERVAL = 8000 as const;
const POLLING_TIMEOUT_MS = 1 * 60 * 1000; // 1 minute

export const QueueStatus: FC = () => {
  const {
    isLoading,
    isLoadingBacklogCount,
    loadQuestionnaireBacklogCount,
    questionnaireBacklogCount,
    loadQuestionnaires,
    selectedQuestionnaireCount,
  } = useContext(QuestionnaireContext);
  const { token } = useContext(UserContext);
  const { isFiltering } = useContext(OverviewContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [previousBacklogCount, setPreviousBacklogCount] = useState<number>(0);
  const [isPollingEnabled, setIsPollingEnabled] = useState<boolean>(true);
  const [showEnqueuedDialog, setShowEnqueuedDialog] = useState<boolean>(false);

  // Update previous backlog count whenever the current count changes
  useEffect(() => {
    if (questionnaireBacklogCount !== previousBacklogCount) {
      setPreviousBacklogCount(questionnaireBacklogCount);
    }
  }, [previousBacklogCount, questionnaireBacklogCount]);

  // Re-enable polling when new questionnaires are added to the queue,
  // to ensure `QueueStatus` doesn't become permanently hidden
  useEffect(() => {
    if (questionnaireBacklogCount > previousBacklogCount) {
      setIsPollingEnabled(true);
    }
  }, [questionnaireBacklogCount, previousBacklogCount]);

  // Trigger one final refresh when count goes from positive to zero
  useEffect(() => {
    if (isFiltering || !!selectedQuestionnaireCount) return;

    if (questionnaireBacklogCount === 0 && previousBacklogCount > 0) {
      setTimeout(async () => {
        await loadQuestionnaires();
      }, 1000);
    }
  }, [
    questionnaireBacklogCount,
    previousBacklogCount,
    loadQuestionnaires,
    isFiltering,
    selectedQuestionnaireCount,
  ]);

  const isHidden: boolean =
    !questionnaireBacklogCount || isLoadingBacklogCount || !isPollingEnabled;

  const handleReload = useCallback(async () => {
    await loadQuestionnaireBacklogCount();
  }, [loadQuestionnaireBacklogCount]);

  // Ensure queue status bar becomes visible on mount if there's items in the queue
  useEffect(() => {
    handleReload();
  }, [handleReload]);

  usePolling(handleReload, QUEUE_COUNT_REFRESH_INTERVAL, {
    enabled: !isHidden && isPollingEnabled,
    pollingTimeoutMs: POLLING_TIMEOUT_MS,
  });

  // Keep refreshing table as long as there are items in the queue being cleared
  usePolling(loadQuestionnaires, TABLE_REFRESH_INTERVAL, {
    enabled:
      !isHidden &&
      !isFiltering &&
      !selectedQuestionnaireCount &&
      (!!questionnaireBacklogCount || previousBacklogCount > 0) &&
      isPollingEnabled,
    pollingTimeoutMs: POLLING_TIMEOUT_MS,
  });

  if (isHidden) return null;

  return (
    <>
      <EnqueuedQuestionnairesDialog
        isOpen={showEnqueuedDialog}
        onClose={() => setShowEnqueuedDialog(false)}
      />
      <Alert type="info" isFlat={true}>
        <Flex isYCentered gap={4} isBetween={true} isFullWidth={true}>
          <span>
            <b>{questionnaireBacklogCount}</b>{' '}
            {questionnaireBacklogCount === 1 ? 'item is' : 'items are'} queued
            and may take a moment to appear.
          </span>
          <Flex isYCentered gap={12}>
            {isLoading && <span>Fetching questionnaires...</span>}
            <ButtonGroup>
              {preferences?.enable_view_of_queued_questionnaires && (
                <Button
                  intent="secondary"
                  size="tiny"
                  value="See what questionnaires are queued to be sent"
                  iconLeft="eye"
                  onClick={() => setShowEnqueuedDialog(true)}
                  withTooltip={true}
                  tooltip="See what questionnaires are queued to be sent"
                  isIconOnly
                />
              )}
              <Button
                intent="secondary"
                size="tiny"
                value="Reload"
                iconLeft="refresh"
                onClick={handleReload}
                withTooltip={true}
                tooltip="Refresh Queue Status"
                isIconOnly
              />
              <Button
                intent="secondary"
                size="tiny"
                value="Stop Polling"
                iconLeft="stop-circle"
                onClick={async () => {
                  setIsPollingEnabled(false);
                  await resetFirmQuestionnaireBacklog({ token });
                }}
                withTooltip={true}
                tooltip="Stop Polling"
                isIconOnly
              />
            </ButtonGroup>
          </Flex>
        </Flex>
      </Alert>
    </>
  );
};
